/**
 * BulkAssignBar — appears under the paste bar while rows are selected:
 * pick a package, confirm once, apply to every selected flow with per-row
 * outcomes in the result toast. Only library trackers' rows (Voluum, Binom)
 * are counted and applied to; selected rows of another tracker (Tres
 * Commas) are named as skipped and never sent.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo, useState, type FC } from 'react';
import { Package } from 'lucide-react';
import { trackerLabel } from '@trafficpulse/shared';

import { Button, SearchableSelect } from '@/components/ui';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { joinWithAnd } from '@/features/optimizer-center/tracker-status/tracker-text';
import { useToast } from '@/providers/ToastProvider';

import { useLibraryPackages } from '../api/hooks/useLibraryPackages';
import AssignConfirmDialog from './AssignConfirmDialog';
import { splitLibraryTargets, useBulkAssign, type BulkAssignOutcome } from './useBulkAssign';

export interface BulkAssignBarProps {
  targets: OptimizerRow[];
  onClearSelection: () => void;
}

/** Explains skipped trackers whose library capability is disabled. */
function skippedNote(skipped: OptimizerRow[]): string {
  const trackers = joinWithAnd([...new Set(skipped.map((r) => trackerLabel(r.provider)))]);
  const flows = skipped.length === 1 ? 'flow' : 'flows';
  return ` (${skipped.length} ${trackers} ${flows} skipped — no package library there)`;
}

function announce(toast: ReturnType<typeof useToast>, outcome: BulkAssignOutcome, name: string) {
  const title = `Applied to ${outcome.ok} of ${outcome.total}`;
  if (outcome.failures.length === 0) {
    toast.success(title, `"${name}" is live on every selected flow.`);
    return;
  }
  toast.error(title, outcome.failures.map((f) => `${f.campaignName}: ${f.reason}`).join('\n'));
}

const BulkAssignBar: FC<BulkAssignBarProps> = ({ targets: selected, onClearSelection }) => {
  const [packageId, setPackageId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const toast = useToast();
  const packages = useLibraryPackages();
  const bulk = useBulkAssign();
  const selectedPackage = useMemo(
    () => (packages.data ?? []).find((pkg) => pkg.id === packageId) ?? null,
    [packages.data, packageId],
  );
  const { targets, skipped } = useMemo(() => {
    const split = splitLibraryTargets(selected);
    return { targets: split.library.map((t) => t.row), skipped: split.skipped };
  }, [selected]);

  if (targets.length === 0) return null;

  const run = () => {
    if (!selectedPackage) return;
    bulk.mutate(
      { packageId: selectedPackage.id, targets },
      {
        onSuccess: (outcome) => {
          setConfirming(false);
          setPackageId('');
          onClearSelection();
          announce(toast, outcome, selectedPackage.name);
        },
      },
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-primary/20 bg-primary/5 px-4 py-2.5">
      <Package className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <span className="text-xs font-medium">
        Assign a package to {targets.length} selected{' '}
        {targets.length === 1 ? 'flow' : 'flows'}
        {skipped.length > 0 ? (
          <span className="font-normal text-muted-foreground">{skippedNote(skipped)}</span>
        ) : null}
      </span>
      <span className="w-56">
        <SearchableSelect
          options={(packages.data ?? []).map((pkg) => ({
            value: pkg.id,
            label: pkg.name,
          }))}
          value={packageId}
          onChange={setPackageId}
          placeholder="Select package…"
        />
      </span>
      <Button
        size="sm"
        className="h-7 px-2.5 text-xs"
        disabled={!selectedPackage || bulk.isPending}
        onClick={() => setConfirming(true)}
      >
        Apply to {targets.length}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-muted-foreground"
        onClick={onClearSelection}
      >
        Clear selection
      </Button>
      <AssignConfirmDialog
        open={confirming}
        packageName={selectedPackage?.name ?? ''}
        formulaSetName={selectedPackage?.formulaSetName}
        ruleSetName={selectedPackage?.ruleSetName}
        count={targets.length}
        flipsStandard={targets.some((row) => row.mode === 'STANDARD')}
        busy={bulk.isPending}
        onConfirm={run}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
};

export default BulkAssignBar;
